import type React from "react";
import { useEffect, useRef } from "react";

import { useDroppable } from "@dnd-kit/core";
import { Stack } from "@mui/material";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";

import type { DurationWeeks, PatientTreatmentScheduleExercise } from "api/services/treatment/schemas/Treatment";
import type { onPinExercise } from "routes/patients/views/Treatment/components/TreatmentEditor/ActivityCard/ActivityCard";
import {
  DropZoneContainer,
  DropZoneIndicator,
} from "routes/patients/views/Treatment/components/TreatmentEditor/shared";
import type {
  HandleLevelChange,
  HandleSetsAndRepsChange,
  HandleWeekRangeChange,
} from "routes/patients/views/Treatment/components/TreatmentEditor/types";
import { Notification } from "shared/atoms/Notification";
import { GREEN } from "theme/colors";

import { ActivityItem } from "./ActivityItem";

interface TreatmentScheduleExercisesProps {
  exercises: PatientTreatmentScheduleExercise[];
  exerciseProps: {
    durationWeeks: DurationWeeks;
    exercisesPerDay: number;
    pinnedExercisesCount: number;
    onLevelChange: HandleLevelChange;
    onPin: onPinExercise;
    onSetsAndRepsChange: HandleSetsAndRepsChange;
    onToggleLevelLock: HandleLevelChange;
    onWeekRangeChange: HandleWeekRangeChange;
  };
  lastAddedExerciseId?: number | null;
  noExercisesError: boolean;
  onPreview: (exercise_id: number, recommended_level: number) => void;
  onRemove: (exercise_id: number, weekNumber?: number) => void;
  onScrolled?: () => void;
}

export const TreatmentScheduleExercises: React.FC<TreatmentScheduleExercisesProps> = ({
  exercises,
  exerciseProps,
  lastAddedExerciseId,
  noExercisesError,
  onPreview,
  onRemove,
  onScrolled,
}) => {
  const { t } = useTranslation();

  const onScrolledRef = useRef(onScrolled);
  onScrolledRef.current = onScrolled;

  useEffect(() => {
    if (lastAddedExerciseId == null) return undefined;
    const el = document.querySelector(`[data-exercise-id="${lastAddedExerciseId}"]`);
    if (!el) return undefined;
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    const timer = setTimeout(() => onScrolledRef.current?.(), 2500);
    return () => clearTimeout(timer);
  }, [lastAddedExerciseId]);

  const { setNodeRef, isOver } = useDroppable({
    id: "exercises",
    data: {
      droppableType: "exercises",
    },
  });

  const highestWeekNumber = exercises.length > 0 ? Math.max(...exercises.flatMap(exercise => exercise.weeks)) : 0;
  const sortedExercises = [...exercises].sort((a, b) => {
    const minWeekA = a.weeks.length > 0 ? Math.min(...a.weeks) : Infinity;
    const minWeekB = b.weeks.length > 0 ? Math.min(...b.weeks) : Infinity;
    return minWeekA - minWeekB;
  });

  return (
    <>
      {noExercisesError && (
        <Notification variant="danger">{t("treatment.editor.feedback.errors.at_least_one_exercise")}</Notification>
      )}

      <DropZoneContainer ref={setNodeRef} className={clsx({ "is-error": noExercisesError, "is-over": isOver })}>
        <Stack spacing={2}>
          {sortedExercises.length > 0 && (
            <Stack spacing={2}>
              {sortedExercises.map(exercise => (
                <ExerciseWrapper
                  key={exercise.exercise_id}
                  data-exercise-id={exercise.exercise_id}
                  $highlight={exercise.exercise_id === lastAddedExerciseId}
                >
                  <ActivityItem
                    activity={exercise}
                    exerciseProps={{
                      ...exerciseProps,
                      highestWeekNumber,
                      onPin: () => exerciseProps.onPin({ exercise_id: exercise.exercise_id }),
                    }}
                    onPreview={() => {
                      onPreview(exercise.exercise_id, exercise.recommended_level || 1);
                    }}
                    onRemove={() => {
                      onRemove(exercise.exercise_id);
                    }}
                  />
                </ExerciseWrapper>
              ))}
            </Stack>
          )}

          <DropZoneIndicator activityType="exercises" />
        </Stack>
      </DropZoneContainer>
    </>
  );
};

const highlightFade = keyframes`
  0% { outline: 2px solid ${GREEN[100]}; }
  80% { outline: 2px solid ${GREEN[100]}; }
  100% { outline: 2px solid transparent; }
`;

const ExerciseWrapper = styled.div<{ $highlight: boolean }>`
  border-radius: 12px;
  scroll-margin-top: 200px;
  ${({ $highlight }) =>
    $highlight &&
    css`
      outline: 2px solid transparent;
      animation: ${highlightFade} 2.5s ease-out forwards;
    `}
`;
